// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FulfillmentMethod } from "@ces/XDL/types";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import changePickUpWarehouse from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/ChangePickUpWarehouse";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import WarehouseAddressInfoDisplay, {
    WarehouseAddressInfoDisplayStyles,
} from "@insite/content-library/Components/WarehouseAddressInfoDisplay";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    fulfillmentMethod: state.components.addressDrawer.fulfillmentMethod,
    pickUpWarehouse: state.components.addressDrawer.pickUpWarehouse,
});

const mapDispatchToProps = {
    changePickUpWarehouse,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AddressDrawerPickUpLocationSelectorStyles {
    wrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    changeLink?: LinkPresentationProps;
    warehouseAddress?: WarehouseAddressInfoDisplayStyles;
    findLocationModal?: FindLocationModalStyles;
    titleWrapper?: InjectableCss;
}

export const addressDrawerPickUpLocationSelectorStyles: AddressDrawerPickUpLocationSelectorStyles = {
    headerText: {
        weight: 600,
    },
    changeLink: {
        css: css`
            margin-left: 15px;
        `,
    },
};

const styles = addressDrawerPickUpLocationSelectorStyles;

const AddressDrawerPickUpLocationSelector = ({ fulfillmentMethod, pickUpWarehouse, changePickUpWarehouse }: Props) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);    
    const handleChangeLocationClick = () => {
        setIsModalOpen(true);
    };
    const handleModalClose = () => setIsModalOpen(false);
    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        changePickUpWarehouse({ warehouse });
        setIsModalOpen(false);
    };
    // XNG-Change: Support new pickup methods
    return (
        <>
            <StyledWrapper
                id="addressDrawerPickUpLocationSelector"
                role="region"
                aria-live="polite"
                data-test-selector="changeCustomer_pickUpLocation"
                {...styles.wrapper}
            >
                {[FulfillmentMethod.PickUpToday, FulfillmentMethod.PickUpScheduled].includes(
                    fulfillmentMethod as any
                ) ? (
                    <>
                        {/* XNG-Change: add wrapper */}
                        <StyledWrapper {...styles.titleWrapper}>
                            <Typography {...styles.headerText}>{translate("Pick Up Address")}</Typography>
                            <Link
                                onClick={handleChangeLocationClick}
                                data-test-selector="changeCustomer_pickUpLocation_findLocation"
                                {...styles.changeLink}
                            >
                                {/* XNG-Change: link label */}
                                {translate("Change Location")}
                            </Link>
                        </StyledWrapper>
                        {pickUpWarehouse && (
                            <WarehouseAddressInfoDisplay
                                warehouse={pickUpWarehouse}
                                extendedStyles={styles.warehouseAddress}
                            />
                        )}
                    </>
                ) : (
                    <></>
                )}
            </StyledWrapper>
            <FindLocationModal
                modalIsOpen={isModalOpen}
                onWarehouseSelected={handleWarehouseSelected}
                onModalClose={handleModalClose}
                extendedStyles={styles.findLocationModal}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AddressDrawerPickUpLocationSelector),
    definition: {
        group: "Header",
        icon: "store",
    },
};

export default widgetModule;
